import React, { useMemo } from 'react';
import { FRAMELESS_GRID_CELL_SIZE_M, OBJECT_TYPES } from '../configuratorConstants';

const DrawingNodes = ({ viewTransform, svgWidth, svgHeight, framelessLastWallId, objects }) => {
  const { x: viewX, y: viewY, scale } = viewTransform;

  const nodes = useMemo(() => {
    if (!svgWidth || !svgHeight || svgWidth === 0 || svgHeight === 0) return [];
    
    const cellSizeWorld = FRAMELESS_GRID_CELL_SIZE_M; // 1.0 метр
    const cellSizeScaled = cellSizeWorld * scale;
    
    // ФИКСИРОВАННЫЙ размер квадратика в пикселях (не зависит от масштаба)
    const nodeSizePx = 6; // 6 пикселей - более заметный размер

    // Скрываем точки при очень маленьком масштабе
    if (cellSizeScaled < 2) return [];

    // Определяем ориентацию последней стены, если она есть
    // Если нет последней стены - показываем все точки (можно начать рисовать с любого места)
    let lastWallOrientation = null; // 'horizontal' или 'vertical'
    let lastWallY = null; // Y координата горизонтальной стены
    let lastWallX = null; // X координата вертикальной стены
    let lastWall = null; // Сохраняем ссылку на последнюю стену
    let hasLastWall = false; // Флаг, что последняя стена найдена
    const epsilon = 0.01;

    if (framelessLastWallId && objects) {
      lastWall = objects.find(obj => 
        obj.type === OBJECT_TYPES.FRAMELESS_WALL && obj.id === framelessLastWallId
      );

      if (lastWall) {
        hasLastWall = true;
        const dx = Math.abs(lastWall.endPoint.x - lastWall.startPoint.x);
        const dy = Math.abs(lastWall.endPoint.y - lastWall.startPoint.y);
        const isHorizontal = Math.abs(dy) < epsilon;
        const isVertical = Math.abs(dx) < epsilon;

        if (isHorizontal) {
          lastWallOrientation = 'horizontal';
          // Если последняя стена горизонтальная, следующая должна быть вертикальной
          lastWallY = lastWall.startPoint.y; // Y координата одинаковая для всей горизонтальной стены
        } else if (isVertical) {
          lastWallOrientation = 'vertical';
          // Если последняя стена вертикальная, следующая должна быть горизонтальной
          lastWallX = lastWall.startPoint.x; // X координата одинаковая для всей вертикальной стены
        }
      }
    }

    // Если нет последней стены - показываем все точки (можно начать рисовать с любого места)

    // Вычисляем видимую область в мировых координатах
    const worldLeft = -viewX / scale;
    const worldTop = -viewY / scale;
    const worldRight = (svgWidth - viewX) / scale;
    const worldBottom = (svgHeight - viewY) / scale;
    
    // Вычисляем индексы сетки
    const startGridX = Math.floor(worldLeft / cellSizeWorld) - 1;
    const startGridY = Math.floor(worldTop / cellSizeWorld) - 1;
    const endGridX = Math.ceil(worldRight / cellSizeWorld) + 1;
    const endGridY = Math.ceil(worldBottom / cellSizeWorld) + 1;
    
    // Ограничиваем количество точек для производительности
    const maxNodes = 5000; // Увеличиваем лимит для покрытия всей области
    const totalNodes = (endGridX - startGridX + 1) * (endGridY - startGridY + 1);
    
    // Вычисляем шаг пропуска точек равномерно, чтобы покрыть всю область
    // Если точек слишком много, пропускаем каждую N-ю точку равномерно
    let skipFactor = 1;
    if (totalNodes > maxNodes) {
      // Вычисляем, какой шаг нужен, чтобы уложиться в лимит
      skipFactor = Math.ceil(Math.sqrt(totalNodes / maxNodes));
    }

    const result = [];

    // Генерируем точки на каждом пересечении сетки с равномерным шагом
    // Это гарантирует, что точки будут распределены по всей видимой области
    for (let i = startGridX; i <= endGridX; i += skipFactor) {
      for (let j = startGridY; j <= endGridY; j += skipFactor) {
        // Координаты точки ТОЧНО на пересечении сетки (кратное cellSizeWorld)
        const worldX = i * cellSizeWorld;
        const worldY = j * cellSizeWorld;
        
        // Фильтруем точки в зависимости от ориентации последней стены
        // Если есть последняя стена - показываем только точки на концах стены (в углах)
        if (hasLastWall && lastWall) {
          let isAtWallEndpoint = false;
          
          if (lastWallOrientation === 'horizontal' && lastWallY !== null) {
            // Если последняя стена горизонтальная, следующая должна быть вертикальной
            // Показываем только точки на концах стены (startPoint и endPoint)
            const isOnWallY = Math.abs(worldY - lastWallY) < epsilon;
            if (isOnWallY) {
              // Проверяем, совпадает ли точка с началом или концом стены
              const distToStart = Math.abs(worldX - lastWall.startPoint.x);
              const distToEnd = Math.abs(worldX - lastWall.endPoint.x);
              isAtWallEndpoint = distToStart < epsilon || distToEnd < epsilon;
            }
          } else if (lastWallOrientation === 'vertical' && lastWallX !== null) {
            // Если последняя стена вертикальная, следующая должна быть горизонтальной
            // Показываем только точки на концах стены (startPoint и endPoint)
            const isOnWallX = Math.abs(worldX - lastWallX) < epsilon;
            if (isOnWallX) {
              // Проверяем, совпадает ли точка с началом или концом стены
              const distToStart = Math.abs(worldY - lastWall.startPoint.y);
              const distToEnd = Math.abs(worldY - lastWall.endPoint.y);
              isAtWallEndpoint = distToStart < epsilon || distToEnd < epsilon;
            }
          }
          
          // Если есть последняя стена, но точка не на её конце - пропускаем
          if (!isAtWallEndpoint) {
            continue;
          }
        }
        // Если нет последней стены (hasLastWall === false) - показываем все точки
        
        // Преобразуем мировые координаты в экранные для фиксированного размера
        // Рендерим в экранных координатах, как Grid
        const screenX = worldX * scale + viewX;
        const screenY = worldY * scale + viewY;
        
        // Проверяем, что точка видна на экране (с небольшим запасом)
        if (screenX < -nodeSizePx * 2 || screenX > svgWidth + nodeSizePx * 2 ||
            screenY < -nodeSizePx * 2 || screenY > svgHeight + nodeSizePx * 2) {
          continue; // Пропускаем точки за пределами видимой области
        }
        
        result.push(
          <rect
            key={`node-${i}-${j}`}
            x={screenX - nodeSizePx / 2}
            y={screenY - nodeSizePx / 2}
            width={nodeSizePx}
            height={nodeSizePx}
            fill="rgba(220, 38, 38, 0.8)"
            stroke="rgba(220, 38, 38, 1)"
            strokeWidth={1.5}
            style={{ pointerEvents: 'none', cursor: 'crosshair' }}
          />
        );
      }
    }

    return result;
  }, [viewX, viewY, scale, svgWidth, svgHeight, framelessLastWallId, objects]);

  // Рендерим точки БЕЗ transform, в экранных координатах (как Grid)
  return <g id="drawing-nodes">{nodes}</g>;
};

export default React.memo(DrawingNodes);